import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import Card from "./Card.jsx";
import Sparkline from "./Sparkline.jsx";
import { colorsFoundation } from "../tokens/colors-foundation.js";
import { colorsSemantic } from "../tokens/colors-semantic.js";
import { dimensions } from "../tokens/dimensions.js";

/**
 * KPICard — the click-dummy stat tile: a muted label, a big value, an optional
 * delta chip (green/red) and an optional mini bar sparkline. Sits on the
 * dashboard in a responsive row.
 */
const DELTA_TONES = {
  positive: { bg: "#dcfce7", fg: "#16a34a" },
  negative: { bg: "#fee2e2", fg: "#dc2626" },
  warning: { bg: "#fef3c7", fg: "#b45309" },
};

export default function KPICard({ label, value, delta, deltaTone = "positive", data, tone }) {
  const deltaColors = DELTA_TONES[deltaTone] || DELTA_TONES.positive;

  return (
    <Card variant="surface" sx={{ height: "100%" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: dimensions.sm }}>
        {/* Label (left) + delta pill (top-right), matching the click dummy. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: dimensions.sm,
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              color: colorsSemantic.text.muted,
            }}
          >
            {label}
          </Typography>
          {delta != null && (
            <Box
              component="span"
              sx={{
                px: "8px",
                py: "2px",
                borderRadius: "20px",
                fontSize: "11px",
                fontWeight: 600,
                backgroundColor: deltaColors.bg,
                color: deltaColors.fg,
                whiteSpace: "nowrap",
                flex: "none",
              }}
            >
              {delta}
            </Box>
          )}
        </Box>

        <Typography
          sx={{
            fontSize: "22px",
            fontWeight: 700,
            lineHeight: 1.1,
            color: colorsSemantic.text.strong,
          }}
        >
          {value}
        </Typography>

        {data && data.length > 0 && (
          <Box sx={{ mt: dimensions.xs }}>
            <Sparkline data={data} tone={tone} />
          </Box>
        )}
      </Box>
    </Card>
  );
}

KPICard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  delta: PropTypes.node,
  deltaTone: PropTypes.oneOf(["positive", "negative", "warning"]),
  data: PropTypes.arrayOf(PropTypes.number),
  tone: PropTypes.string,
};
